import {
  Avatar, Box, Chip, Dialog, DialogContent,
  Divider, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { fDateTime } from 'src/utils/formatTime';
import { getAudioByEvent } from 'src/api/clientDetails';
import AuditVideoPlayer from './AuditVideoPlayer';

export default function AuditPlayer({ player, setPlayer, updateToClient }) {
  const IsAgentBased = localStorage.getItem('IsAgentBased');
  const [announcementFile, setAnnounceMentData] = useState([]);

  useEffect(() => {
    if (player.EventNo) {
      getAudioByEvent({ event_no: player.EventNo }, (response) => {
        if (response.status === 200) setAnnounceMentData(response.data);
      });
    }
  }, [player.EventNo]);

  const handleClose = () => setPlayer((plyr) => ({ ...plyr, isOpen: false }));

  const emojiMap = {
    ':calendar:': '📅', ':round_pushpin:': '📍', ':rotating_light:': '🚨',
    ':dart:': '🎯', ':satellite_antenna:': '📡', ':pushpin:': '📌',
    ':loud_sound:': '🔊', ':blue_book:': '📘', ':memo:': '📝',
  };

  const formatComment = (comment = '') => {
    if (!comment) return '';
    const cid = localStorage.getItem('client_id');
    let formatted = comment.replace(/color:\s*black;?/gi, '');
    Object.entries(emojiMap).forEach(([shortcode, emoji]) => {
      formatted = formatted.replace(new RegExp(`${shortcode}\\s*(.*?):`, 'g'), (_, label) => `<strong>${emoji} ${label}:</strong>`);
    });
    let sections = formatted.trim().split(/\n{2,}/)
      .map((block) => `<p style="margin:0 0 6px 0;line-height:1.4;">${block.replace(/\n/g, '<br />')}</p>`)
      .join('');
    if (cid === '1316') sections = sections.replace(/redcar /g, 'TP ');
    else if (cid === '1320') sections = sections.replace(/redcar /g, 'GL ');
    return sections;
  };

  const toTime = player.CreatedOn
    ? new Date(new Date(player.CreatedOn).getTime() + 60000).toISOString()
    : player.EventTime
      ? new Date(new Date(player.EventTime).getTime() + 60000).toISOString()
      : null;

  const infoContent = (
    <Box>
      {/* Event details */}
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>EVENT DETAILS</Typography>
      {[
        { label: 'Camera', value: player.DeviceName },
        { label: 'Event No', value: player.EventNo },
        { label: 'Type', value: player.DetectedType },
        { label: 'Time', value: (player.EventTime || player.CreatedOn) ? fDateTime(player.EventTime || player.CreatedOn) : null },
      ].filter((r) => r.value).map(({ label, value }) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0, mr: 1 }}>{label}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.primary', textAlign: 'right' }}>{value}</Typography>
        </Box>
      ))}

      {/* Detected object chips */}
      {player.detectiontype && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>DETECTED OBJECTS</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {player.detectiontype.split(',').map((v, k) => (
              <Chip key={k} size="small" label={v.trim()} sx={{ textTransform: 'capitalize' }}
                avatar={<Avatar src={player.icon} sx={{ bgcolor: 'info.light' }} />} />
            ))}
          </Box>
        </>
      )}

      {/* IsAgentBased=0 detected event types */}
      {IsAgentBased === '0' && player.DetectedEventType && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {player.DetectedEventType.split(',').map((v, k) => (
              <Chip key={k} size="small" sx={{ textTransform: 'capitalize' }}
                avatar={<Avatar>{v.charAt(0).toUpperCase()}</Avatar>} label={v} />
            ))}
          </Box>
        </>
      )}

      {/* Comment */}
      {player.SupervisorComments && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>COMMENT</Typography>
          <Box sx={{
            fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary',
            '& ol, & ul': { display: 'block !important', margin: '8px 0 !important', paddingLeft: '20px !important', color: 'text.primary' },
            '& ol': { listStyleType: 'decimal !important' },
            '& ul': { listStyleType: 'disc !important' },
            '& li': { display: 'list-item !important', margin: '4px 0 !important' },
            '& p': { margin: '6px 0 !important', color: 'text.primary' },
          }}>
            <div dangerouslySetInnerHTML={{ __html: formatComment(player.SupervisorComments) }} />
          </Box>
        </>
      )}

      {/* Audio announcements */}
      {announcementFile.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.08em', mb: 1 }}>AUDIO ANNOUNCEMENTS</Typography>
          {announcementFile.map((row, i) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.FileName}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>×{row.NoOfTimesAnnounced}</Typography>
              </Box>
              <audio controls src={row.FilePath} type="audio/mpeg" style={{ width: '100%', height: 32 }} />
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  return (
    <Dialog
      fullScreen
      open={player.isOpen}
      PaperProps={{ sx: { overflow: 'hidden', bgcolor: 'background.default' } }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100vh' }}>
        <AuditVideoPlayer
          sourceUrl={player.VideoUrl}
          cameraLabel={player.DeviceName}
          cameraId={player.CameraId}
          eventNo={player.EventNo}
          fromTime={player.CreatedOn || player.EventTime}
          toTime={toTime}
          onClose={handleClose}
          infoContent={infoContent}
        />
      </DialogContent>
    </Dialog>
  );
}

AuditPlayer.propTypes = {
  player: PropTypes.object.isRequired,
  setPlayer: PropTypes.func.isRequired,
};
